import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { MultiValue } from 'react-select';

import { ItemBase, Item, SelectOpt, Company, CompanyOpt } from '@/types';
import { useDeleteItemProperty } from '@/features/item-properties/api';

import { useUpdateItem, useDeleteItem, uploadItemIcon } from '../api';

/**
 * Update Item catalogue Modal
 * @date 2025/03/24
 * @param selectedItem
 * @param activeCompanies
 * @param handleClose
 * @returns
 */
export const useItemUpdateModal = (
  selectedItem: Item,
  activeCompanies: Company[],
  handleClose: () => void
) => {
  const { id, ...remaining } = selectedItem;
  // == translate item to updatable payload for save later ==
  const [payload, setPayload] = useState<Omit<ItemBase, 'id'>>({
    type: remaining.type,
    category: remaining.category,
    classification_type: remaining.classification_type,
    companies: remaining.companies.map((c) => (c as CompanyOpt).company_id),
    description: remaining.description,
    ui_visible: remaining.ui_visible,
  });
  const [propertiesToDelete, setPropertiesToDelete] = useState<string[]>([]);
  // == svg icon file
  const [iconFile, setIconFile] = useState<File>();

  // == update handler ==
  const updateItemMutation = useUpdateItem({
    itemId: id,
  });
  // delete propery handler
  const deleteItemProperty = useDeleteItemProperty({});
  // delete item handler
  const deleteItemMutation = useDeleteItem({
    itemId: id,
    onSuccess: () => handleClose(),
  });

  const companyToSelectOpt = (c: Company) => ({
    label: c.name,
    value: c.id as number,
  });
  const companyOptions = activeCompanies.map(companyToSelectOpt);
  // composed company option in default value!
  const companiesInUse = selectedItem.companies.map((c) => {
    const { company_id } = c as { company_id: number };
    const companyObj = activeCompanies.find((c) => c.id == company_id);
    if (!companyObj) {
      console.warn(`## company not found: ${company_id} !`);
      return { label: `Unkown-com:${company_id}`, value: company_id };
    }
    // company id supposed to be `number`!
    return { label: companyObj.name, value: Number(companyObj.id) };
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPayload((prevPayload) => ({ ...prevPayload, [name]: value }));
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;
    setPayload((prevPayload) => ({ ...prevPayload, [name]: value }));
  };

  // edit item property
  const handleCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name: property, checked } = event.target;

    if (checked) {
      setPropertiesToDelete((state) => state.filter((p) => p === property));
    } else {
      setPropertiesToDelete((state) => [...state, property]);
    }
  };
  // update company list
  const handleCompanySelect = (values: MultiValue<SelectOpt>) => {
    const companies = values.map((o) => o.value);
    setPayload({ ...payload, companies });
  };

  // == save it ===
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // close first!
    handleClose();

    // update properties!
    await Promise.allSettled(
      propertiesToDelete.map((property) =>
        deleteItemProperty.mutateAsync({ id, name: property })
      )
    );
    // updae others...
    await updateItemMutation.mutateAsync({ payload });
  };

  const handleItemDelete = () => {
    deleteItemMutation.mutateAsync();
  };

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    const [selectedFile] = Array.from(event.target.files);
    setIconFile(selectedFile);
  };

  const handleSVGUpload = () => {
    if (!iconFile) {
      return console.warn('## select svg file to upload!');
    }

    toast.promise(uploadItemIcon(iconFile, id), {
      loading: 'uploading...',
      success: 'Icon uploded!',
      error: 'Error when uploading!',
    });
  };

  return {
    companyOptions,
    companiesInUse,
    payload,
    handleInputChange,
    handleCheckbox,
    handleSelectChange,
    handleCompanySelect,
    /** save update */
    handleSubmit,
    handleItemDelete,
    handleFileSelected,
    handleSVGUpload,
  };
};
